import { createReducer, on } from '@ngrx/store'
import {
  createFormControlState,
  createFormGroupState,
  formGroupReducer,
  formStateReducer,
  onNgrxForms,
  onNgrxFormsAction,
  SetValueAction,
  updateGroup,
  validate,
  wrapReducerWithFormStateUpdate,
} from 'ngrx-forms'
import { email, required } from 'ngrx-forms/validation'
import { ChangeEmailValues, ChangePasswordValues, CustomerInformationState, ProfileState } from './user-profile.state'
import { CustomerInfoActions } from './user-profile.actions'
import { RootActions } from '../../app.root.actions'

const EMAIL_FORM_ID = 'change-email-form'
const PHONE_NUMBER_FORM_ID = 'change-phone-number-form'
const MOBILE_FORM_ID = 'change-mobile-form'
const BIRTHDATE_FORM_ID = 'change-birthdate-form'
const CLOSE_ACCOUNT_FORM_ID = 'close-account-form'
const CHANGE_EMAIL_PASSWORD_PROMPT_FORM_ID = 'change-email-password-prompt-form'
const CHANGE_PASSWORD_FORM_ID = 'change-password-form'
const FORGOT_PASSWORD_FORM_ID = 'forgot-password-form'

const initialCustomerInformationState: CustomerInformationState = {
  phoneNumber: '',
  mobile: '',
  email: '',
  customerNumber: '',
  customerSubNumber: '',
  customerSince: '',
  birthdate: '',
  address: {
    firstName: '',
    lastName: '',
    city: '',
    countryCode: '',
    company: '',
    postalCode: '',
    street: '',
    street2: '',
  },
  firstName: '',
  lastName: '',
  uuid: '',
}

const initialEmailFormState = createFormGroupState<ChangeEmailValues>(EMAIL_FORM_ID, {
  newMail: '',
  repeatMail: '',
})

const initialChangePasswordFormState = createFormGroupState<ChangePasswordValues>(CHANGE_PASSWORD_FORM_ID, {
  oldPassword: '',
  newPassword: '',
  repeatPassword: '',
})

enum PendingState {
  pending = 'pending',
  success = 'success',
  failed = 'failed',
}

const initialPhoneNumberFormState = createFormControlState(PHONE_NUMBER_FORM_ID, '')
const initialMobileFormState = createFormControlState(MOBILE_FORM_ID, '')
const initialBirthdateFormState = createFormControlState(BIRTHDATE_FORM_ID, '')
const initialCloseAccountFormState = createFormControlState(CLOSE_ACCOUNT_FORM_ID, '')
const initialChangeEmailPasswordPromptFormState = createFormControlState(CHANGE_EMAIL_PASSWORD_PROMPT_FORM_ID, '')
const initialForgotPasswordFormState = createFormControlState(FORGOT_PASSWORD_FORM_ID, '')
const initialKeycloakState = {
  kcUuid: '',
  kcEmail: '',
  kcFirstName: '',
  kcLastName: '',
}

const initialState: ProfileState = {
  changePhoneNumberControl: initialPhoneNumberFormState,
  changeMobileControl: initialMobileFormState,
  changeBirthdateControl: initialBirthdateFormState,
  changeEmailControls: initialEmailFormState,
  customerInformationState: initialCustomerInformationState,
  closeAccountControl: initialCloseAccountFormState,
  changeEmailPasswordPromptControl: initialChangeEmailPasswordPromptFormState,
  changePasswordControls: initialChangePasswordFormState,
  forgotPasswordControl: initialForgotPasswordFormState,
  paymentMethodsState: [],
  paymentMethodsPendingState: PendingState.pending,
  c4sessionId: '',
  c4LoginState: PendingState.pending,
  keycloakData: initialKeycloakState,
  adrGr: '',
  isGroupAdmin: false,
}

export interface KeycloakData {
  kcUuid: string
  kcEmail: string
  kcFirstName: string
  kcLastName: string
}

export const validateChangeEmailForm = updateGroup<ChangeEmailValues>({
  //todo: further validation (whether both emails are equal, email isn't used by other acc, etc.)
  newMail: validate(required, email),
  repeatMail: validate(required, email),
})

export const rawAppReducer = createReducer(
  initialState,
  onNgrxForms(),
  onNgrxFormsAction(SetValueAction, (state, action) => ({
    ...state,
    changeEmailControls: formGroupReducer(state.changeEmailControls, action),
    changePhoneNumberControls: formStateReducer(state.changePhoneNumberControl, action),
    changeMobileControls: formStateReducer(state.changeMobileControl, action),
    closeAccountControl: formStateReducer(state.closeAccountControl, action),
    changeEmailPasswordPromptControl: formStateReducer(state.changeEmailPasswordPromptControl, action),
    changePasswordControls: formStateReducer(state.changePasswordControls, action),
    forgotPasswordControl: formStateReducer(state.forgotPasswordControl, action),
  })),
  on(
    CustomerInfoActions.customerInfoSuccess,
    (state, action): ProfileState => ({
      ...state,
      customerInformationState: action.payload,
    }),
  ),
  on(
    CustomerInfoActions.c4LoginLoad,
    (state, action): ProfileState => ({
      ...state,
      c4LoginState: PendingState.pending,
    }),
  ),
  on(
    CustomerInfoActions.c4LoginSuccess,
    (state, action): ProfileState => ({
      ...state,
      c4LoginState: PendingState.success,
      c4sessionId: action.payload.sessionId,
      adrGr: action.payload.adrGr,
      isGroupAdmin: action.payload.isUserGroupAdmin ?? false,
    }),
  ),
  on(
    CustomerInfoActions.c4LoginFail,
    (state, action): ProfileState => ({
      ...state,
      c4LoginState: PendingState.failed,
    }),
  ),
  on(
    CustomerInfoActions.paymentMethodsLoad,
    (state, action): ProfileState => ({
      ...state,
      paymentMethodsPendingState: PendingState.pending,
    }),
  ),
  on(
    CustomerInfoActions.paymentMethodsFail,
    (state, action): ProfileState => ({
      ...state,
      paymentMethodsPendingState: PendingState.failed,
    }),
  ),
  on(
    CustomerInfoActions.paymentMethodsSuccess,
    (state, action): ProfileState => ({
      ...state,
      paymentMethodsState: action.payload,
      paymentMethodsPendingState: PendingState.success,
    }),
  ),
  on(
    RootActions.setKcAttributes,
    (state, action): ProfileState => ({
      ...state,
      keycloakData: {
        kcUuid: action.payload.kcUuid,
        kcEmail: action.payload.kcEmail,
        kcFirstName: action.payload.kcFirstName,
        kcLastName: action.payload.kcLastName,
      },
    }),
  ),
)

export const profileReducer = wrapReducerWithFormStateUpdate(
  rawAppReducer,
  s => s.changeEmailControls,
  validateChangeEmailForm,
)
